import { useEffect, useId, useRef, useState } from 'react'

import type { CatalogListDto } from '../api/catalogLists'

type CatalogListDialogProps = {
  open: boolean
  itemName: string
  lists: CatalogListDto[]
  selectedListIds: number[]
  busy?: boolean
  onClose: () => void
  onToggle: (listId: number, selected: boolean) => void
  onCreate: (name: string) => Promise<void>
}

/** One compact dialog for assigning a product to any number of organisation lists. */
export const CatalogListDialog = ({
  open,
  itemName,
  lists,
  selectedListIds,
  busy = false,
  onClose,
  onToggle,
  onCreate,
}: CatalogListDialogProps) => {
  const titleId = useId()
  const initialFocus = useRef<HTMLButtonElement | null>(null)
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setName('')
      setCreateError(null)
      return
    }
    const frame = window.requestAnimationFrame(() => initialFocus.current?.focus())
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose, open])

  if (!open) return null

  const handleCreate = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    setCreating(true)
    setCreateError(null)
    try {
      await onCreate(trimmed)
      setName('')
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Не удалось создать список')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="market-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="market-dialog market-catalog-lists-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="market-dialog__header">
          <div>
            <h2 id={titleId} className="market-dialog__title">Списки</h2>
            <p className="market-dialog__hint">{itemName}</p>
          </div>
          <button
            ref={initialFocus}
            type="button"
            className="echat-btn echat-btn--ghost echat-btn--sm"
            onClick={onClose}
          >
            Закрыть
          </button>
        </div>

        <div className="market-catalog-lists-dialog__choices" role="group" aria-label="Списки позиции">
          {lists.map((list) => {
            const selected = selectedListIds.includes(list.id)
            return (
              <label key={list.id} className="market-catalog-lists-dialog__choice">
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={busy || creating}
                  onChange={(event) => onToggle(list.id, event.target.checked)}
                />
                <span>{list.name}</span>
              </label>
            )
          })}
        </div>

        <div className="market-catalog-lists-dialog__create">
          <span className="echat-field__label">Новый список</span>
          <div className="market-catalog-lists-dialog__create-row">
            <input
              className="echat-field__input"
              value={name}
              maxLength={80}
              placeholder="Например, Постоянный заказ"
              disabled={busy || creating}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void handleCreate()
                }
              }}
            />
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={!name.trim() || busy || creating}
              onClick={() => void handleCreate()}
            >
              {creating ? 'Создание…' : 'Создать'}
            </button>
          </div>
        </div>
        {createError ? <p className="echat-error" role="alert">{createError}</p> : null}
      </section>
    </div>
  )
}
